import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { getColorScheme } from '../../private/helpers/getColorScheme';

const defaultColorSchemeOptions = ['standard', 'inherit'] as const;

export type DefaultColorSchemeTypes = (typeof defaultColorSchemeOptions)[number];

const semanticColorSchemeOptions = ['error', 'info', 'success', 'warning'] as const;

export type SemanticColorSchemeTypes = (typeof semanticColorSchemeOptions)[number];

export const brandColorSchemeOptions = [
  'blue',
  'green',
  'orange',
  'pink',
  'purple',
  'red',
  'yellow',
] as const;

export type BrandColorSchemeTypes = (typeof brandColorSchemeOptions)[number];

export const vendorColorSchemeOptions = [
  'vendor-hubspot',
  'vendor-marketo',
  'vendor-pardot',
] as const;

export type VendorColorSchemeTypes = (typeof vendorColorSchemeOptions)[number];

export const colorSchemeOptions = [
  ...defaultColorSchemeOptions,
  ...semanticColorSchemeOptions,
  ...brandColorSchemeOptions,
  ...vendorColorSchemeOptions,
] as const;

export type ColorSchemeTypes = (typeof colorSchemeOptions)[number];

type StyledColorSchemeWrapperProps = {
  $nav: boolean;
  $colorScheme: ColorSchemeTypes;
  children: ReactNode;
};

const StyledColorSchemeWrapper = styled.div<StyledColorSchemeWrapperProps>`
  ${({ $colorScheme, $nav }) => getColorScheme($nav ? 'nav' : $colorScheme)};
`;

export type ColorSchemeWrapperProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * @ignore For use in creating navs - will override any colorScheme prop
   */
  __nav?: boolean;

  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Children
   */
  children: ReactNode;
};

/**
 * A wrapper to apply a color scheme to its children.
 */
export const ColorSchemeWrapper = ({
  __nav = false,
  colorScheme = 'inherit',
  children,
  ...props
}: ColorSchemeWrapperProps): JSX.Element => (
  <StyledColorSchemeWrapper
    $colorScheme={colorScheme}
    $nav={__nav}
    {...props}
  >
    {children}
  </StyledColorSchemeWrapper>
);

ColorSchemeWrapper.displayName = 'ColorSchemeWrapper';
